import { Header } from "@/components/universal/header";
import {
  ParamDataTableToolbar,
  ParamDateFilter,
} from "@/components/ui/param-data-table";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import FulfillmentMetricCards from "./-components/metric-cards";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import TimePeriodDropdown from "@/components/ui/time-period-dropdown";
import { AssistantFilterBar } from "@/components/universal/filter-assistant/filter-bar";
import {
  dateRangeWithGroupingField,
  entityLookupField,
  timePeriodField,
} from "@/components/universal/filter-assistant/presets";
import { getAutoTimePeriod } from "@/lib/time-period";
import { useEffectiveDateRange } from "@/hooks/use-effective-date-range";
import { getThisMonth } from "@/lib/get-date-ranges";
import { createFileRoute } from "@tanstack/react-router";
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";
import {
  TimePeriodEnum,
  EntityTypeEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { useGetEntities } from "@/requests/gen/react-query/entity";
import FulfilledSubordersChart from "./-components/fulfilled-suborders-chart";
import AvgFulfillmentTimeChart from "./-components/avg-fulfillment-time-chart";

export const Route = createFileRoute(
  "/_private/_fulfillment-360/fulfillment360-analytics/",
)({
  component: Fulfillment360Analytics,
});

function Fulfillment360Analytics() {
  const [queryStates, setQueryStates] = useQueryStates({
    time_period: parseAsStringEnum(Object.values(TimePeriodEnum)),
    fulfiller_ids: parseAsArrayOf(parseAsInteger),
    created_at_start: parseAsString,
    created_at_end: parseAsString,
  });

  const { data: fulfillers } = useGetEntities({
    entity_types: [EntityTypeEnum.Fulfiller],
  });

  const onlyEntity = useOnlyEntity();

  const fulfillerOptions = (fulfillers?.items || []).map((f) => ({
    label: f.name,
    value: f.entity_id,
  }));

  const {
    effectiveDateStart: effectiveCreatedAtStartIso,
    effectiveDateEnd: effectiveCreatedAtEndIso,
    effectiveTimePeriod,
    markDateFilterTouched,
  } = useEffectiveDateRange({
    dateStart: queryStates.created_at_start,
    dateEnd: queryStates.created_at_end,
    timePeriod: queryStates.time_period,
  });
  const effectiveCreatedAtStart = effectiveCreatedAtStartIso?.split("T")[0];
  const effectiveCreatedAtEnd = effectiveCreatedAtEndIso?.split("T")[0];

  const kpiParams = {
    fulfiller_ids: queryStates.fulfiller_ids,
    created_at_start: effectiveCreatedAtStart,
    created_at_end: effectiveCreatedAtEnd,
  };

  const chartParams = {
    ...kpiParams,
    time_period: effectiveTimePeriod,
  };

  return (
    <div className="pb-4">
      <title>Fulfillment Analytics - Legrande Health Portal</title>
      <Header
        crumbs={[
          {
            name: "Fulfillment 360 Analytics",
            link: { to: "/fulfillment360-analytics" },
          },
        ]}
      />
      <AssistantFilterBar
        fields={[
          dateRangeWithGroupingField({
            name: "created_at",
            description: "The order creation date range to show.",
            current: {
              start: effectiveCreatedAtStart,
              end: effectiveCreatedAtEnd,
            },
            onApply: ({ start, end }, autoTimePeriod) =>
              setQueryStates({
                created_at_start: start,
                created_at_end: end,
                time_period: autoTimePeriod,
              }),
          }),
          timePeriodField({
            current: effectiveTimePeriod,
            onApply: (time_period) => setQueryStates({ time_period }),
          }),
          ...(onlyEntity
            ? []
            : [
                entityLookupField({
                  name: "fulfiller_ids",
                  entityType: "Fulfiller",
                  summary: "The fulfiller(s) whose orders are shown",
                  currentIds: queryStates.fulfiller_ids,
                  getLabel: (id) =>
                    fulfillers?.items?.find((f) => f.entity_id === id)?.name,
                  onApply: (ids) => setQueryStates({ fulfiller_ids: ids }),
                }),
              ]),
        ]}
      >
        <ParamDataTableToolbar alwaysVisibleCount={0} overflowCount={Infinity}>
          <ParamDateFilter
            title="Created At"
            name="created_at"
            defaultValue={getThisMonth()}
            onDateChange={(dateRange) => {
              markDateFilterTouched();
              if (dateRange?.from && dateRange?.to) {
                setQueryStates({
                  time_period: getAutoTimePeriod(dateRange.from, dateRange.to),
                });
              }
            }}
          />

          {!onlyEntity && (
            <DropdownFilter
              title="Fulfiller"
              options={[{ items: fulfillerOptions }]}
              values={queryStates.fulfiller_ids || []}
              onChange={(values) => setQueryStates({ fulfiller_ids: values })}
            />
          )}

          <TimePeriodDropdown
            value={effectiveTimePeriod}
            onChange={(time_period) => setQueryStates({ time_period })}
          />
        </ParamDataTableToolbar>
      </AssistantFilterBar>
      <div className="grid mb-4 grid-cols-1 sm:grid-cols-3 gap-4">
        <FulfillmentMetricCards params={kpiParams} />
      </div>

      <div className="space-y-4">
        <AvgFulfillmentTimeChart params={chartParams} />
        <FulfilledSubordersChart params={chartParams} />
      </div>
    </div>
  );
}
